/**
 * 主导航的信息架构（数据与渲染分离，便于机器复核）。
 *
 * 分区结构照 console 定稿（经营 / 广告 / 商品 / 系统）落成应用的
 * **经营 / 广告 / 商品 / 数据**：多了「数据」（应用有真实 `/v1/sync/status`），
 * 少了「系统」（「设置」移到左下角入口）。分区顺序本身就是 IA 的一部分，不要重排。
 *
 * 2026-10-03：**恢复「商品」分区**。此前砍掉它的原因是货号看板还没接真实主数据；
 * 现在商品 / SKU / 货号主数据（迁移 0002）与品牌级分类树（0014）已就绪，
 * 「商品管理」有真实数据可读。分类树配置从商品页的按钮进入，不占主导航项。
 *
 * `badge` 同时是 G 前缀快捷键的数字键，必须唯一且连续。
 */

/** 路由联合类型：新增路由时必须同时在这里 + router.tsx 登记（两处不一致会被 TS 拦住）。 */
export type RoutePath =
  | "/"
  | "/overview"
  | "/spend-overview"
  | "/products"
  | "/products/categories"
  | "/style-codes"
  | "/creatives"
  | "/ad-management"
  | "/google-campaigns"
  | "/sync";

export interface NavItem {
  readonly to: RoutePath;
  readonly label: string;
  readonly hint: string;
  readonly section: string;
  readonly badge: string;
  /** 20×20 线性图标 path（与 console 的 NAV_ICONS 同款几何） */
  readonly icon: string;
}

/** 分区顺序固定，渲染时不再排序。 */
// 「设置」移到左下角入口后，「系统」分区已空 —— 空分区不放进导航（点进去是空的，正是「没用的内容」）。
export const NAV_SECTION_ORDER: readonly string[] = ["经营", "广告", "商品", "数据"];

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: "/overview",
    label: "经营概览",
    hint: "MER · 真实收入 · 令牌到期提醒",
    section: "经营",
    badge: "1",
    icon: "M3 15V9m4 6V5m4 10v-4m4 4V7",
  },
  {
    // 2026-10-05 第十九批 · 用户第 2 条：「花费总览」与「概览」「广告管理」并列。
    // 归「经营」分区（紧接着经营概览，与概览同属品牌维度的经营视图）。
    // ⚠️ badge 用 "7" 而**不是**插在 1 与 2 之间：badge 同时是 G 前缀快捷键的数字键
    // （`AppShell` 的 `NAV_ITEMS.find(item => item.badge === key)`），重编号会**改掉既有快捷键**，
    // 因此新增项一律取下一个空闲数字（badge 不参与渲染，只决定快捷键）。
    to: "/spend-overview",
    label: "花费总览",
    hint: "Meta / Google 花费 × 销售额占比 · 按月 / 按天",
    section: "经营",
    badge: "7",
    icon: "M3 16V7m4 9V4m4 12v-6m4 6V9",
  },
  {
    to: "/",
    label: "广告指标表",
    hint: "实体表 · 暂停/启动 · 改预算",
    section: "广告",
    badge: "2",
    icon: "M3 6h14v9H3zM7 18h6",
  },
  {
    to: "/ad-management",
    label: "广告管理",
    hint: "折叠树 · 三分组 · 一键启用子级",
    section: "广告",
    badge: "3",
    icon: "M4 4h5v3H4zM11 4h5v3h-5zM4 11h5v3H4zM11 11h5v3h-5zM6.5 7v2h7V7M6.5 14v1.5h7V14",
  },
  {
    to: "/style-codes",
    label: "货号看板",
    hint: "站点级 · 货号 × 广告 · USD 折算",
    // ⚠️ 归「广告」分区（2026-10-04 用户口径）：R23 原文就是「**货号看板 = 站点级广告管理**
    // （受站点选择器约束）」，且用户明确要求「广告指标、广告管理、货号看板、素材看板，
    // 都在广告这个主栏目，且都跟随右上角品牌+店铺」。
    // 实现时一度被放进「商品」分区，与语义不符。
    section: "广告",
    badge: "4",
    icon: "M3 4h14v3H3zM3 9h6v7H3zM11 9h6v3h-6zM11 14h6v2h-6z",
  },
  {
    // 2026-10-06（task-23 · C2）：**素材看板**。用户口径（R23 起的判定一直没变）：
    // 「广告指标、广告管理、货号看板、素材看板，都在广告这个主栏目，且都跟随右上角品牌+店铺」
    // ⇒ 归「广告」分区，紧跟货号看板（两个都是店铺级看板）。
    // `badge` 取下一个空闲数字 "9"（badge 同时是 G 快捷键，只追加、不重排 —— 见文件头）。
    to: "/creatives",
    label: "素材看板",
    hint: "店铺级 · 素材名聚合 / 重复在投 · 批量暂停",
    section: "广告",
    badge: "9",
    icon: "M3 5h14v10H3zM6 8h5M6 11h3M14 8.5 15.5 10 14 11.5",
  },
  {
    // 2026-10-05 第二十批 · 用户需求：Google Ads 系列层管理（基础版）。
    // 归「广告」分区，紧接货号看板；`badge` 取下一个空闲数字 "8"（badge 同时是 G 快捷键，
    // 只能追加、不能重排——见本文件头部说明）。
    to: "/google-campaigns",
    label: "Google 系列",
    hint: "结构快照 · 暂停/启用 · 改日预算",
    section: "广告",
    badge: "8",
    icon: "M4 5h12v10H4zM7 8h6M7 11h4M4 18h12",
  },
  {
    to: "/products",
    label: "商品管理",
    hint: "货号 · SKU · 分类 · US 价",
    section: "商品",
    badge: "5",
    icon: "M3 5h14v4H3zM3 11h14v4H3zM6 7h.01M6 13h.01",
  },
  {
    to: "/sync",
    label: "数据同步",
    hint: "水位 · 行数 · 失败原因",
    section: "数据",
    badge: "6",
    icon: "M10 3v10m0 0 3.5-3.5M10 13 6.5 9.5M4 15v2h12v-2",
  },
];

/** 按分区顺序分组；空分区不会被产出（渲染层不用再判空）。 */
export function groupNavItems(
  items: readonly NavItem[] = NAV_ITEMS,
  order: readonly string[] = NAV_SECTION_ORDER,
): readonly { readonly section: string; readonly items: readonly NavItem[] }[] {
  return order
    .map((section) => ({ section, items: items.filter((item) => item.section === section) }))
    .filter((group) => group.items.length > 0);
}
